import type { ReactNode } from "react";
import type { LucideIcon } from "lucide-react";
import {
  ChevronDown,
  Circle,
  Crown,
  Gem,
  Heart,
  PanelTop,
  PartyPopper,
  Pencil,
  Ribbon,
  Rows3,
  ScanLine,
  ShieldCheck,
  Shuffle,
  Sparkles,
  Square,
  Upload,
  Users,
} from "lucide-react";

// Preset type definition
export interface Preset {
  label: string;
  value: string;
  icon: ReactNode;
}

const presetIconClassName = "size-5";

function presetIcon(Icon: LucideIcon): ReactNode {
  return (
    <Icon className={presetIconClassName} strokeWidth={1.75} aria-hidden />
  );
}

// Front dress presets
export const FRONT_PRESETS: Preset[] = [
  {
    label: "Wedding",
    value: "A romantic wedding dress with elegant details",
    icon: presetIcon(Heart),
  },
  {
    label: "Formal",
    value: "Sophisticated formal evening gown",
    icon: presetIcon(Gem),
  },
  {
    label: "Bridesmaid",
    value: "Elegant bridesmaid dress",
    icon: presetIcon(Users),
  },
  {
    label: "Quinceanera",
    value: "Modern princess quinceanera dress",
    icon: presetIcon(Crown),
  },
  {
    label: "Prom",
    value: "Glamorous prom dress",
    icon: presetIcon(PartyPopper),
  },
  {
    label: "Modest",
    value: "Modest wedding dress that is elegant",
    icon: presetIcon(ShieldCheck),
  },
  {
    label: "Upload",
    value: "",
    icon: presetIcon(Upload),
  },
  {
    label: "Custom",
    value: "",
    icon: presetIcon(Pencil),
  },
];

// Back dress presets
export const BACK_PRESETS: Preset[] = [
  {
    label: "Low V-Back",
    value: "Low V-back with clean lines that complements the front design",
    icon: presetIcon(ChevronDown),
  },
  {
    label: "Corset Lace-Up",
    value: "Corset-style lace-up back with boning and satin laces",
    icon: presetIcon(Ribbon),
  },
  {
    label: "Illusion Back",
    value: "Illusion mesh back with floral lace appliqués and covered buttons",
    icon: presetIcon(ScanLine),
  },
  {
    label: "Keyhole Back",
    value: "Circular keyhole back with delicate edging and refined finish",
    icon: presetIcon(Circle),
  },
  {
    label: "Crisscross Straps",
    value: "Crisscross straps over an open back with adjustable spacing",
    icon: presetIcon(Shuffle),
  },
  {
    label: "Button Row",
    value: "Covered buttons down the back over an invisible zipper",
    icon: presetIcon(Rows3),
  },
  {
    label: "Zip-Up Back",
    value:
      "Clean zip-up back with invisible zipper for easy wear and modern appeal",
    icon: presetIcon(PanelTop),
  },
  {
    label: "Bow Tie Back",
    value: "Soft bow at the lower back with flowing tails",
    icon: presetIcon(Sparkles),
  },
  {
    label: "Square Back",
    value: "Structured square back line with supportive construction",
    icon: presetIcon(Square),
  },
  {
    label: "Upload",
    value: "",
    icon: presetIcon(Upload),
  },
  {
    label: "Custom",
    value: "",
    icon: presetIcon(Pencil),
  },
];

// URL parameter mapping for category presets
// Maps URL parameter values to preset values
export const URL_PRESET_MAP: Record<string, string> = {
  wedding: "A romantic wedding dress with elegant details",
  formal: "Sophisticated formal evening gown",
  bridesmaid: "Elegant bridesmaid dress",
  quinceanera: "Modern princess quinceanera dress",
  prom: "Glamorous prom dress",
  modest: "Modest wedding dress that is elegant",
  upload: "", // Special case - handled separately
};

// Helper function to get preset by URL parameter
export function getPresetByUrlParam(param: string): string | null {
  if (param === "upload") {
    return null; // Upload is handled specially
  }
  return URL_PRESET_MAP[param] || null;
}

// Helper function to get all presets for a section
export function getPresetsForSection(sectionType: "front" | "back"): Preset[] {
  return sectionType === "front" ? FRONT_PRESETS : BACK_PRESETS;
}

// Helper function to find preset by value
export function findPresetByValue(
  value: string,
  sectionType: "front" | "back"
): Preset | undefined {
  const presets = getPresetsForSection(sectionType);
  return presets.find(preset => preset.value === value);
}

// Helper function to check if a prompt matches a preset value
export function promptMatchesPreset(
  prompt: string,
  presetValue: string
): boolean {
  if (!presetValue) return false; // Custom and Upload have empty values
  const normalizedPrompt = prompt.trim().toLowerCase();
  const normalizedPresetValue = presetValue.trim().toLowerCase();
  return (
    normalizedPrompt === normalizedPresetValue ||
    normalizedPrompt.startsWith(`${normalizedPresetValue} `) ||
    normalizedPrompt.startsWith(`${normalizedPresetValue},`) ||
    normalizedPrompt.startsWith(`${normalizedPresetValue}.`)
  );
}

const KNOWN_PRESET_LABELS = [
  "Wedding",
  "Formal",
  "Bridesmaid",
  "Quinceanera",
  "Prom",
] as const;

// Helper function to get the actual preset label from formData
// Prefers preset_type when it's a known preset (not Custom)
export function getPresetLabel(
  formData: {
    prompt?: string;
    backPrompt?: string;
    preset_type?: string | null;
    isFrontUploadPreset?: boolean;
    isBackUploadPreset?: boolean;
    uploadedFrontImages?: File[];
    uploadedBackImages?: File[];
  },
  sectionType: "front" | "back"
): string | null {
  const isFront = sectionType === "front";
  const prompt = isFront ? formData.prompt || "" : formData.backPrompt || "";
  const uploadFlag = isFront
    ? formData.isFrontUploadPreset
    : formData.isBackUploadPreset;
  const uploadedImages = isFront
    ? formData.uploadedFrontImages
    : formData.uploadedBackImages;

  // Check if upload preset is active
  if (uploadFlag || (uploadedImages && uploadedImages.length > 0)) {
    return "Upload";
  }

  // Prefer explicit preset_type when it's a known preset (not Custom)
  const explicitPreset = formData.preset_type?.trim();
  if (
    explicitPreset &&
    KNOWN_PRESET_LABELS.includes(
      explicitPreset as (typeof KNOWN_PRESET_LABELS)[number]
    )
  ) {
    return explicitPreset;
  }

  // Find the matching preset and return its label
  const presets = getPresetsForSection(sectionType);
  for (const preset of presets) {
    // Skip Custom and Upload presets as they have empty values
    if (preset.label === "Custom" || preset.label === "Upload") {
      continue;
    }
    if (promptMatchesPreset(prompt, preset.value)) {
      return preset.label;
    }
  }

  // If prompt exists but doesn't match any preset, it's custom
  if (prompt.trim().length > 0) {
    return "Custom";
  }

  // Empty prompt defaults to null (unknown)
  return null;
}

// Helper function to determine preset type from formData
export type PresetType = "preset" | "custom" | "upload";

export function getPresetType(
  formData: {
    prompt?: string;
    backPrompt?: string;
    isFrontUploadPreset?: boolean;
    isBackUploadPreset?: boolean;
    isFrontCustomPreset?: boolean;
    isBackCustomPreset?: boolean;
    uploadedFrontImages?: File[];
    uploadedBackImages?: File[];
  },
  sectionType: "front" | "back"
): PresetType {
  const isFront = sectionType === "front";
  const prompt = isFront ? formData.prompt || "" : formData.backPrompt || "";
  const uploadFlag = isFront
    ? formData.isFrontUploadPreset
    : formData.isBackUploadPreset;
  const customFlag = isFront
    ? formData.isFrontCustomPreset
    : formData.isBackCustomPreset;
  const uploadedImages = isFront
    ? formData.uploadedFrontImages
    : formData.uploadedBackImages;

  // Check if upload preset is active (highest priority)
  if (uploadFlag || (uploadedImages && uploadedImages.length > 0)) {
    return "upload";
  }

  // Check if custom preset is explicitly selected (second priority)
  if (customFlag) {
    return "custom";
  }

  // Check if prompt matches any preset value
  const presets = getPresetsForSection(sectionType);
  const matchesPreset = presets.some(preset => {
    // Skip Custom and Upload presets as they have empty values
    if (preset.label === "Custom" || preset.label === "Upload") {
      return false;
    }
    return promptMatchesPreset(prompt, preset.value);
  });

  if (matchesPreset) {
    return "preset";
  } else if (prompt.trim().length > 0) {
    // Has prompt but doesn't match preset - must be custom
    return "custom";
  }

  // Empty prompt defaults to "preset"
  return "preset";
}

// Backward compatibility exports
export const DRESS_PRESETS = FRONT_PRESETS;
export const DRESS_PRESETS_BACK = BACK_PRESETS;
